<template>
  <div class="rs-grouped-button-select">
    <label v-if="label">{{ label }}<sup v-if="required">*</sup></label>
    <div class="d-flex button-group justify-content-between">
      <div
        v-for="option in options"
        :key="option.value"
        class="d-flex button-group-item-wrapper"
      >
        <RsButton
          :variant="boundValue === option.value ? 'secondary-light' : 'border'"
          :class="{
            selected: boundValue === option.value,
            unselected: boundValue !== option.value,
          }"
          :disabled="disabled"
          @click="$emit('input', option.value); $emit('update:modelValue', option.value)"
        >
          {{ option.text }}
        </RsButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';
import { ISelectOption } from '@/shared';

export default defineComponent({
  name: 'RsFormGroupedButtonSelect',
  props: {
    value: {
      type: String,
      required: true,
    },
    /**
     * Vue 3 v-model binding, kept alongside the legacy `value`/`input` pair so
     * both `v-model` and explicit `:value`+`@input` consumers work. `undefined`
     * means the parent did not bind v-model (see the `boundValue` computed).
     */
    modelValue: {
      type: String,
      default: undefined,
    },
    options: {
      type: Array as PropType<ISelectOption[]>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      required: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input', 'update:modelValue'],
  data() {
    return {
      selected: this.value,
    };
  },

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): string {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.rs-grouped-button-select {
  margin-bottom: 1rem;

  .unselected {
    color: $medium-grey;
  }
  .button-group-item-wrapper {
    flex-grow: 1;
    .rs-button {
      display: flex;
      flex-grow: 1;
      &--disabled.selected {
        background-color: $secondary-color;
        color: $white;
      }
    }
    &:first-child .rs-button {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    &:last-child .rs-button {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    &:not(:first-child):not(:last-child) .rs-button {
      border-radius: 0;
    }
  }
}
</style>
